import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { ConnectSiemModal } from '@/components/siem/ConnectSiemModal';
import { DisconnectSiemDialog } from '@/components/siem/DisconnectSiemDialog';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { useDeleteOrgSetting, useOrgSetting } from '@/hooks/api/useOrgSettings';
import { useToast } from '@/hooks/use-toast';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';
import { NotFoundError } from '@kirin/api-client';
import {
  SettingType,
  siemConfigBaseSchema,
  type SiemConfig,
} from '@kirin/api-client/contracts/settings';

import { SiemIntegrationCard } from './SiemIntegrationCard';

// Code lives on `data`, not `response` — `errorResponseSchema` strips it.
function isSettingNotFoundError(error: unknown): boolean {
  if (!(error instanceof NotFoundError)) return false;
  const data = error.data;
  if (typeof data !== 'object' || data === null) return false;
  return (data as { code?: unknown }).code === 'SETTING_NOT_FOUND';
}

function parseSiemConfig(
  rawConfig: unknown,
  organizationId: string
): Partial<SiemConfig> {
  if (rawConfig === undefined || rawConfig === null) {
    return {};
  }
  const result = siemConfigBaseSchema.partial().safeParse(rawConfig);
  if (!result.success) {
    Sentry.captureException(result.error, {
      tags: { error_type: 'siem_config_parse' },
      extra: { organizationId },
    });
    return {};
  }
  return result.data;
}

interface SiemIntegrationProps {
  access: RenderableIntegrationAccess;
}

export function SiemIntegration({
  access,
}: SiemIntegrationProps): ReactElement {
  if (access.canRender) return <SiemIntegrationInner access={access} />;
  return <SiemIntegrationCard siemSettings={undefined} isLoading />;
}

SiemIntegration.displayName = 'SiemIntegration';

function SiemIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement | null {
  const { organizationId, isLoading: isAccessLoading } = access;

  const {
    data: siemSettings,
    isLoading: siemLoading,
    isError: siemIsError,
    error: siemQueryError,
  } = useOrgSetting(organizationId, SettingType.Siem);
  const deleteMutation = useDeleteOrgSetting(organizationId, SettingType.Siem);
  const { toast } = useToast();

  const isNotConfigured = siemIsError && isSettingNotFoundError(siemQueryError);
  const siemError =
    siemIsError && !isNotConfigured
      ? getErrorMessage(siemQueryError)
      : undefined;

  useEffect(() => {
    if (!siemIsError || isNotConfigured) return;
    Sentry.captureException(siemQueryError, {
      tags: { error_type: 'siem_settings_fetch' },
      extra: { organizationId },
    });
  }, [siemIsError, isNotConfigured, siemQueryError, organizationId]);

  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const openConnect = (): void => setConnectOpen(true);
  const openDisconnect = (): void => setDisconnectOpen(true);
  const closeDisconnect = (): void => setDisconnectOpen(false);

  const handleConfirmDisconnect = (): void => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setDisconnectOpen(false);
        toast({
          title: 'SIEM disconnected',
          description: 'SIEM forwarding has been disabled.',
        });
      },
      onError: error => {
        Sentry.captureException(error, {
          tags: { error_type: 'siem_disconnect' },
          extra: { organizationId },
        });
        toast({
          title: 'Failed to disconnect SIEM',
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      },
    });
  };

  const siemConfig = useMemo(
    () => parseSiemConfig(siemSettings?.config, organizationId),
    [siemSettings?.config, organizationId]
  );
  const isCardLoading = isAccessLoading || siemLoading;

  return (
    <>
      <SiemIntegrationCard
        siemSettings={siemSettings}
        onConnect={openConnect}
        onManage={openConnect}
        onDisconnect={openDisconnect}
        isLoading={isCardLoading}
        error={siemError}
      />
      <ConnectSiemModal
        key={organizationId}
        open={connectOpen}
        onOpenChange={setConnectOpen}
        organizationId={organizationId}
        initialConfig={siemConfig}
        readOnly={!access.canWrite}
      />
      <DisconnectSiemDialog
        isOpen={disconnectOpen}
        onClose={closeDisconnect}
        onConfirm={handleConfirmDisconnect}
        isLoading={deleteMutation.isPending}
        readOnly={!access.canWrite}
      />
    </>
  );
}

SiemIntegrationInner.displayName = 'SiemIntegrationInner';
